/**
 * The day timeline, as the Main board draws it (25 September 2026, issue #49):
 * one clock axis for the day, a row per vehicle, each trip a span from its
 * departure to its return with its stops on it, so a turnaround and the
 * arrivals at the factory show.
 *
 * Every trip is a link to the same day with `?trip=<route_id>`, because the
 * selection lives in the address and a selected trip can be sent to a
 * colleague. A link is also what makes the timeline keyboard operable without
 * any code of its own: each trip is in the tab order, in the order it is drawn,
 * and Enter selects it. The marks on a span are drawn for the eye and hidden
 * from assistive technology; the link's name carries the same facts in words,
 * and the inspector lists every stop.
 *
 * Positions are percentages of the axis, computed here and set inline. That is
 * layout, not a design token; every colour and space is a token in
 * `timeline.css`.
 */
import { Section } from "@depicomnlbv/platform-ui";
import { Link } from "react-router-dom";

import type { Day, Trip } from "../api";
import { formatClock, minutesIntoDay } from "../time";
import { tripLabels, vehicleRows } from "../trips";

/** The Main board's axis runs 04:00 to 22:00; a trip outside it widens it. */
const DEFAULT_START_H = 4;
const DEFAULT_END_H = 22;

export interface Span {
  trip: Trip;
  start: number;
  end: number;
}

/** A trip's extent in minutes: departure to return, or its first and last
 *  stop when the planner wrote no departure or return. Null when it has no
 *  time at all, and then it cannot be drawn. */
export function spanOf(trip: Trip, day: Day): Span | null {
  const first = trip.departure ?? trip.stops[0]?.arrival ?? null;
  const last = trip.return ?? trip.stops[trip.stops.length - 1]?.departure ?? null;
  if (first === null || last === null) return null;
  const start = minutesIntoDay(first, day.time_zone, day.date);
  const end = minutesIntoDay(last, day.time_zone, day.date);
  return { trip, start, end: Math.max(end, start) };
}

export const percent = (value: number) => `${value.toFixed(3)}%`;

/** The clock hour of an axis hour. The axis counts from the day's midnight,
 *  so the evening before is -2 and the small hours after are 25; both are
 *  written as the clock shows them. `%` keeps the sign in JavaScript, hence
 *  the second step. */
export function hourLabel(h: number): string {
  return `${String(((h % 24) + 24) % 24).padStart(2, "0")}:00`;
}

function stopCount(trip: Trip): string {
  return trip.stops.length === 1 ? "1 stop" : `${trip.stops.length} stops`;
}

export function tripName(trip: Trip, label: string, vehicle: string, day: Day): string {
  const parts = [label, vehicle];
  if (trip.departure) parts.push(`departs ${formatClock(trip.departure, day.time_zone, day.date)}`);
  if (trip.return) parts.push(`back at factory ${formatClock(trip.return, day.time_zone, day.date)}`);
  parts.push(stopCount(trip));
  return parts.join(", ");
}

export function Timeline({ day, selected }: { day: Day; selected: number | null }) {
  const rows = vehicleRows(day.trips);
  const labels = tripLabels(day.trips);
  const spans = new Map<number, Span | null>(day.trips.map((t) => [t.route_id, spanOf(t, day)]));
  const drawn = [...spans.values()].filter((s): s is Span => s !== null);

  const startH = Math.min(DEFAULT_START_H, ...drawn.map((s) => Math.floor(s.start / 60)));
  const endH = Math.max(DEFAULT_END_H, ...drawn.map((s) => Math.ceil(s.end / 60)));
  const axisStart = startH * 60;
  const axisLength = (endH - startH) * 60;
  const at = (minute: number) => ((minute - axisStart) / axisLength) * 100;
  const step = endH - startH > 12 ? 2 : 1;
  const ticks: number[] = [];
  for (let h = startH; h <= endH; h += step) ticks.push(h);

  const undrawn = day.trips.filter((t) => spans.get(t.route_id) === null);

  return (
    <Section
      className="timeline-panel"
      empty={rows.length === 0 ? "There are no trips to draw on this day." : undefined}
      title="Timeline"
    >
      {rows.length === 0 ? null : (
        // Wide on purpose, so it scrolls inside its own region at a phone's
        // width rather than making the page scroll sideways (WCAG 1.4.10).
        // Not a tab stop of its own: every trip in it is a link, and focusing
        // one scrolls it into view, so the keyboard reaches all of it anyway.
        <div
          aria-label="Timeline, scrolls sideways on a narrow screen"
          className="timeline__scroll"
          role="region"
        >
          <div className="timeline">
            <div aria-hidden="true" className="timeline__axis">
              {ticks.map((h) => (
                <span className="timeline__tick" key={h} style={{ left: percent(at(h * 60)) }}>
                  {hourLabel(h)}
                </span>
              ))}
            </div>
            <ul aria-label="Vehicles and their trips" className="timeline__rows" role="list">
              {rows.map((row) => (
                <li className="timeline__row" key={row.key}>
                  <span className="timeline__vehicle">{row.name}</span>
                  <div className="timeline__track">
                    {row.trips.map((trip) => {
                      const span = spans.get(trip.route_id);
                      if (!span) return null;
                      const label = labels.get(trip.route_id) ?? "Trip";
                      const length = Math.max(span.end - span.start, 1);
                      const isSelected = trip.route_id === selected;
                      return (
                        <Link
                          aria-current={isSelected ? "true" : undefined}
                          aria-label={tripName(trip, label, row.name, day)}
                          className={
                            isSelected
                              ? "timeline__trip timeline__trip--selected"
                              : "timeline__trip"
                          }
                          key={trip.route_id}
                          style={{
                            left: percent(at(span.start)),
                            width: percent((length / axisLength) * 100),
                          }}
                          to={{ search: `?trip=${trip.route_id}` }}
                        >
                          <span className="timeline__label">{label}</span>
                          {trip.stops.map((stop) => (
                            <span
                              aria-hidden="true"
                              className="timeline__stop"
                              key={stop.stop_seq}
                              style={{
                                left: percent(
                                  ((minutesIntoDay(stop.arrival, day.time_zone, day.date) -
                                    span.start) /
                                    length) *
                                    100,
                                ),
                              }}
                            />
                          ))}
                          {trip.return ? (
                            <span aria-hidden="true" className="timeline__return" />
                          ) : null}
                        </Link>
                      );
                    })}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {undrawn.length > 0 ? (
        <p className="timeline__undrawn">
          Not on the timeline, because the planner wrote no time for them:{" "}
          {undrawn.map((t) => labels.get(t.route_id)).join(", ")}.
        </p>
      ) : null}
    </Section>
  );
}
